import axios from 'axios';
import type { User } from 'types/users';
import type { GetUsersQueryParams } from 'store/slices/users/users.slice';
import type { Note } from 'components/Admin/types';
import type {
  AcceptInvitationRequest,
  InvitedUser,
} from 'components/Admin/organisms/Accounts/molecules/NewStaffAccount/useNewStaffAccount';
import type { FilesDto } from 'store/slices/cars/types';
import { endpoints } from 'store/endpoints';
import { axiosInstance, BASE_URL } from './axiosInstance';

export class UsersService {
  static async verifyAccount(token: string) {
    try {
      const response = await axiosInstance.post(
        endpoints.users.verifyAccount,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async sendVerificationEmail() {
    try {
      await axiosInstance.post(endpoints.users.sendVerificationEmail);
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getCustomerById(id: string) {
    try {
      const response = await axiosInstance.get(
        endpoints.users.getCustomerById(id),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async createUserWithoutPassword(payload: Partial<User>) {
    try {
      const formData = new FormData();
      const { passport, drivingLicense, ...rest } = payload;
      if (passport) {
        formData.append('passport', passport as File);
      }
      if (drivingLicense) {
        formData.append('drivingLicense', drivingLicense as File);
      }

      formData.append('body', JSON.stringify(rest));
      const response = await axiosInstance.post(
        endpoints.users.createWithoutPassword,
        formData,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getFile(file: Partial<FilesDto>) {
    try {
      if (!file.mediaUrl) {
        return;
      }

      const response = await axios.get(file.mediaUrl, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${file.title}.${response.data.type.substring(
          response.data.type.lastIndexOf('/') + 1,
        )}`,
      );
      document.body.appendChild(link);
      link.click();
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async resetPassword(email: string) {
    try {
      await axiosInstance.post(endpoints.users.resetPassword, { email });
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async setNewPassword(payload: {
    newPassword: string;
    confirmNewPassword: string;
    token: string;
  }) {
    try {
      const { token, ...creds } = payload;
      const response = await axiosInstance.patch<{
        user: User;
        accessToken: string;
      }>(endpoints.users.setNewPassword, creds, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updatePassword(credentials: {
    confirmNewPassword: string;
    newPassword: string;
    currentPassword: string;
  }) {
    try {
      await axiosInstance.patch(endpoints.users.updatePassword, credentials);
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async login(payload: { email: string; password: string }) {
    try {
      const response = await axiosInstance.post<{
        user: User;
        accessToken: string;
      }>(endpoints.users.login, {
        email: payload.email,
        password: btoa(payload.password),
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async socialLogin(accessToken: string) {
    try {
      const response = await axios.post(
        `${BASE_URL}${endpoints.users.socialLogin}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(
        err?.response?.data?.message
          ? err.response.data.message
          : 'Something went wrong',
      );
    }
  }
  static async getMe() {
    try {
      const response = await axiosInstance.get<User>(endpoints.users.getMe);
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async getUserById(id: string) {
    try {
      const response = await axiosInstance.get(endpoints.users.opWithId(id));
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async updateUser(payload: Partial<User>) {
    try {
      const { passport, drivingLicense, proofOfAddressDoc, id, ...body } =
        payload;
      if (!id) {
        return;
      }
      const formData = new FormData();
      if (passport instanceof File) {
        formData.append('passport', passport as File);
      }
      if (drivingLicense instanceof File) {
        formData.append('drivingLicense', drivingLicense as File);
      }

      if (proofOfAddressDoc instanceof File) {
        formData.append('proofOfAddressDoc', proofOfAddressDoc as File);
      }

      formData.append('body', JSON.stringify(body));
      const response = await axiosInstance.patch(
        endpoints.users.opWithId(id),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async deleteUser(id: string) {
    try {
      await axiosInstance.delete(endpoints.users.opWithId(id));
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async acceptInvitation(payload: AcceptInvitationRequest) {
    try {
      const { token, ...body } = payload;
      const response = await axiosInstance.patch(
        endpoints.users.acceptInvitation,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return {
        user: response.data.user,
        accessToken: response.data.accessToken,
      };
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async inviteUser(payload: InvitedUser) {
    try {
      const response = await axiosInstance.post(
        endpoints.users.invite,
        payload,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getAllStaffAccounts() {
    try {
      const response = await axiosInstance.get<Array<User>>(
        endpoints.users.getAllStaffAccounts,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async getAllOwnerAccounts() {
    try {
      const response = await axiosInstance.get<Array<User>>(
        endpoints.users.getAllOwnerAccounts,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async getUserByEmail(email: string) {
    try {
      const response = await axios.get(
        `${BASE_URL}${endpoints.users.getUserByEmail(email)}`,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async registerUser(body: User) {
    try {
      const { drivingLicense, passport, proofOfAddressDoc, ...rest } = body;
      const formData = new FormData();
      formData.append('passport', passport as File);
      formData.append('drivingLicense', drivingLicense as File);
      formData.append('proofOfAddressDoc', proofOfAddressDoc as File);

      formData.append('body', JSON.stringify(rest));
      const result = await axiosInstance.post(
        endpoints.users.registerUser,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return result.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async getAllUsers(params: Partial<GetUsersQueryParams>) {
    try {
      const response = await axiosInstance.get(endpoints.users.general, {
        params,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async archiveUsers(ids: Array<string>) {
    try {
      const response = await axiosInstance.patch(
        endpoints.users.archiveUsers(ids),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async removeCustomers(ids: Array<string>) {
    try {
      const response = await axiosInstance.delete(
        endpoints.users.removeUsers(ids),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async getUserNotes() {
    try {
      const response = await axiosInstance.get(endpoints.notes.general);
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async createNote(note: Omit<Note, 'id' | 'userId'>) {
    try {
      const response = await axiosInstance.post(endpoints.notes.general, note);
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }

  static async deleteNote(noteId: string) {
    try {
      const response = await axiosInstance.delete(
        endpoints.notes.opWithId(noteId),
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
  static async updateNote(note: Note) {
    try {
      const response = await axiosInstance.patch(
        endpoints.notes.opWithId(note.id),
        note,
      );
      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  }
}
